import type { ChangeEvent } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineBugIcon from 'LuxKit/Icons/line/LineBugIcon'
import LineChatBubbleUserIcon from 'LuxKit/Icons/line/LineChatBubbleUserIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ContactContext from 'contexts/contactContext'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Clickable from 'components/Common/Clickable'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import type { BasicTrip } from 'tripPlanner/types/common'
import Switch from 'LuxKit/Switch/Switch'
import Sheet from 'components/utils/Sheet'
import { buildRecentChatItems, getRecentChatItemUrl, type RecentChatItem } from 'tripPlanner/pages/chat/recentChatItems'
import DrawerRecentChatListItem from './DrawerRecentChatListItem'
import TripPlannerMobileHeaderTitle from './TripPlannerMobileHeaderTitle'
import TripPlannerMobileFooter from './TripPlannerMobileFooter'
import { TRIP_PLANNER_FEEDBACK_SURVEY_URL } from './tripPlannerDrawerConfig'

const MOBILE_VARIANT_CLASS_NAME = 'mobile'

const HeaderLogoWrapper = styled.div`
  display: flex;
  align-items: center;
`

const DrawerModalBody = styled(ModalBody)`
  overflow: hidden;
  min-height: 0;
`

const DrawerModalContent = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  min-height: 0;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    padding: 0;
  }
`

const DrawerLayout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(12)};
  min-height: 0;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    gap: 0;
  }
`

const ChatsSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${rem(16)};
  min-height: 0;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    padding: ${rem(16)} ${rem(20)} 0;
  }
`

const ChatHistoryList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  &.${MOBILE_VARIANT_CLASS_NAME} {
    margin: 0 -${rem(20)};
  }
`

const DrawerBottomControls = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: ${rem(28)};

  &.${MOBILE_VARIANT_CLASS_NAME} {
    gap: ${rem(8)};
    padding: ${rem(12)} ${rem(16)} calc(${rem(12)} + env(safe-area-inset-bottom));
  }
`

interface Props {
  variant?: 'default' | 'mobile'
  onStartNewChat: () => void
  recentChats?: Array<TripPlannerChat>
  recentTrips?: Array<BasicTrip>
  isRecentItemsLoading?: boolean
  onChatSelect?: (chatId: number) => void
  onChatRename?: (chatId: number, name: string) => Promise<void>
  onTripRename?: (tripId: string, name: string) => Promise<void>
  onChatDelete?: (chatId: number) => Promise<void> | void
  onTripDelete?: (tripId: string) => Promise<void> | void
  downloadItineraryAction?: React.ReactNode
  // Staff tools
  isStaff?: boolean
  isDevMode?: boolean
  onDevModeToggle?: () => void
}

function TripPlannerDrawer({
  variant = 'default',
  onStartNewChat,
  recentChats,
  recentTrips,
  isRecentItemsLoading = false,
  onChatSelect,
  onChatRename,
  onTripRename,
  onChatDelete,
  onTripDelete,
  downloadItineraryAction,
  isStaff = false,
  isDevMode = false,
  onDevModeToggle,
}: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext()
  const supportContact = useContext(ContactContext)
  const isMobileVariant = variant === 'mobile'
  const mobileVariantClassName = isMobileVariant ? MOBILE_VARIANT_CLASS_NAME : undefined

  const closeDrawer = useCallback(() => {
    resolve()
  }, [resolve])

  const handleNewChat = useCallback(() => {
    closeDrawer()
    onStartNewChat()
  }, [closeDrawer, onStartNewChat])

  const handleRecentItemClick = useCallback((item: RecentChatItem) => (event: React.MouseEvent) => {
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
    if (isModifiedClick) {
      return
    }

    closeDrawer()
    if (item.type === 'aiChat') {
      event.preventDefault()
      onChatSelect?.(item.id)
    }
  }, [closeDrawer, onChatSelect])

  const handleDeleteClick = useCallback(
    (item: RecentChatItem, closeMenu: () => void) => (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      closeMenu()
      closeDrawer()
      if (item.type === 'aiChat') {
        onChatDelete?.(item.id)
        return
      }
      onTripDelete?.(item.id)
    },
    [closeDrawer, onChatDelete, onTripDelete],
  )

  const handleRename = useCallback(
    async(item: RecentChatItem, name: string) => {
      if (item.type === 'aiChat') {
        await onChatRename?.(item.id, name)
        return
      }
      await onTripRename?.(item.id, name)
    },
    [onChatRename, onTripRename],
  )

  const handleDevModeToggle = useCallback((_event: ChangeEvent<HTMLInputElement>) => {
    closeDrawer()
    onDevModeToggle?.()
  }, [closeDrawer, onDevModeToggle])

  const allRecentItems = useMemo(() => {
    return buildRecentChatItems(recentChats ?? [], recentTrips ?? [])
  }, [recentChats, recentTrips])
  const hasRecentItems = allRecentItems.length > 0
  const showRecentItemsLoading = isRecentItemsLoading && !hasRecentItems
  const chatsLabel = intl.formatMessage({
    id: 'x3JBSvTY-jXIoAlF0Kiw',
    defaultMessage: 'Chats',
    description: 'Section heading for the chats list in the trip planner drawer',
  })
  const createNewChatLabel = intl.formatMessage({
    id: 'hMLFrteXDG4txmeo29R7',
    defaultMessage: 'Create new chat',
    description: 'Button label to start a new chat in the trip planner drawer',
  })
  const planWithAiChatHeading = intl.formatMessage({
    id: 'LBjr7kIwyT6PI1I_-koY',
    defaultMessage: 'Plan with AI chat',
    description: 'Heading for the empty state when no chats exist in the drawer',
  })
  const planWithAiChatBody = intl.formatMessage({
    id: 'w3ssyhVL6Z7hEsPnhNhc',
    defaultMessage: 'Explore ideas, shape your trip, and refine plans as you go.',
    description: 'Description for the empty state when no chats exist in the drawer',
  })
  const giveFeedbackTitle = intl.formatMessage({
    id: 'XQlLVgnHoOF0Vvfrs3F5',
    defaultMessage: 'Give feedback',
    description: 'Title for the give feedback link in the TripPlanner navigation drawer menu',
  })
  const giveFeedbackSubtitle = intl.formatMessage({
    id: 'EFHzgukGnljGQ2-yKUwj',
    defaultMessage: 'We want to hear your thoughts on TripPlanner',
    description: 'Subtitle encouraging users to share feedback about the TripPlanner feature',
  })
  const needSupportTitle = intl.formatMessage({
    id: 'FbRO4l6WxkGkA3OmUAPm',
    defaultMessage: 'Need support? Call us 24/7',
    description: 'Title for the customer support link in the TripPlanner navigation drawer',
  })

  const staffToolsText = intl.formatMessage({
    id: 'MVS8v_NHiDbWbR0NuItv',
    defaultMessage: 'Staff tools',
    description: 'Label for the staff-only tools section in the TripPlanner navigation drawer',
  })

  const devModeText = intl.formatMessage({
    id: 'dXMxtVabgKHFm0Fe0oMH',
    defaultMessage: 'Dev mode',
    description: 'Toggle label for enabling developer mode in the TripPlanner navigation drawer',
  })

  const defaultHeaderTitle = useMemo(() => (
    <HeaderLogoWrapper>
      <Clickable to="">
        <LogoIcon height={24} desktopVariant="icon+text" />
      </Clickable>
    </HeaderLogoWrapper>
  ), [])

  const mobileHeaderTitle = useMemo(() => <TripPlannerMobileHeaderTitle />, [])

  const headerTitle = isMobileVariant ? mobileHeaderTitle : defaultHeaderTitle

  const staffTools = isStaff && (
    <Group direction="vertical" gap={12}>
      <BodyText variant="medium" weight="bold">{staffToolsText}</BodyText>

      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <LineBugIcon />
          <BodyText variant="medium">{devModeText}</BodyText>
        </Group>
        <Switch
          checked={isDevMode}
          onChange={handleDevModeToggle}
        />
      </Group>
    </Group>
  )

  return (
    <ModalBase mode="menu">
      <ModalHeader title={headerTitle} onCloseButtonClick={closeDrawer} />
      <DrawerModalBody>
        <DrawerModalContent className={mobileVariantClassName} flushListItems>
          <DrawerLayout className={mobileVariantClassName}>
            <ChatsSection className={mobileVariantClassName}>
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="center">
                {isMobileVariant && (
                  <BodyText variant="medium" weight="bold" colour="light-foreground-alt">
                    {chatsLabel}
                  </BodyText>
                )}
                {!isMobileVariant && (
                  <BodyText variant="small" colour="light-foreground-alt">
                    {chatsLabel}
                  </BodyText>
                )}
                {isMobileVariant && (
                  <TextLink size="caption" colour="normal-contrast" onClick={handleNewChat}>
                    {createNewChatLabel}
                  </TextLink>
                )}
                {!isMobileVariant && (
                  <TextButton kind="tertiary" size="small" onClick={handleNewChat}>
                    {createNewChatLabel}
                  </TextButton>
                )}
              </Group>

              {hasRecentItems && (
                <ChatHistoryList className={mobileVariantClassName}>
                  {allRecentItems.map(item => (
                    <DrawerRecentChatListItem
                      key={`${item.type}-${item.id}`}
                      item={item}
                      subtitle={item.subtitle}
                      description={item.type === 'aiChat' ? item.description : undefined}
                      to={getRecentChatItemUrl(item)}
                      onClick={handleRecentItemClick(item)}
                      onRename={handleRename}
                      onDeleteClick={handleDeleteClick}
                    />
                  ))}
                </ChatHistoryList>
              )}
              {showRecentItemsLoading && (
                <LoadingIndicator
                  data-testid="trip-planner-drawer-recent-items-loading"
                  inline
                  backdrop="none"
                  height={96}
                />
              )}
              {!isRecentItemsLoading && !hasRecentItems && (
                <Sheet padding="12 20" background="light-background" borderRadius="M">
                  <Group direction="vertical" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      {planWithAiChatHeading}
                    </BodyText>
                    <BodyText variant="small" colour="light-foreground-alt">
                      {planWithAiChatBody}
                    </BodyText>
                  </Group>
                </Sheet>
              )}
            </ChatsSection>

            <DrawerBottomControls className={mobileVariantClassName}>
              {isMobileVariant && (
                <TripPlannerMobileFooter
                  closeDrawer={closeDrawer}
                  downloadItineraryAction={downloadItineraryAction}
                  giveFeedbackTitle={giveFeedbackTitle}
                  giveFeedbackSubtitle={giveFeedbackSubtitle}
                  needSupportTitle={needSupportTitle}
                  staffTools={staffTools}
                />
              )}
              {!isMobileVariant && (
                <>
                  {downloadItineraryAction}
                  <Group direction="vertical" gap={8}>
                    <ClickableListItem
                      title={giveFeedbackTitle}
                      subtitle={giveFeedbackSubtitle}
                      startIcon={<LineChatBubbleUserIcon />}
                      href={TRIP_PLANNER_FEEDBACK_SURVEY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                    <ClickableListItem
                      title={needSupportTitle}
                      subtitle={supportContact.local.humanReadable}
                      startIcon={<LinePhoneIcon />}
                      href={`tel:${supportContact.local.number}`}
                    />
                  </Group>
                  {staffTools}
                </>
              )}
            </DrawerBottomControls>
          </DrawerLayout>
        </DrawerModalContent>
      </DrawerModalBody>
    </ModalBase>
  )
}

export default TripPlannerDrawer
